<template>
    <div class="main-reasons-report">
        <div class="card shadow-sm border-0">

            <HeaderFilter
                title="Principais Motivos de Reservas"
                @apply-filters="loadData"
            />

            <div class="card-body">
                <!-- Loading -->
                <div
                    v-if="loadingData"
                    class="text-center py-4"
                >
                    <i class="fas fa-spinner fa-spin fa-2x text-purple"></i>
                    <p class="mt-2">Carregando motivos...</p>
                </div>

                <!-- Conteúdo Principal -->
                <div v-else>
                    <!-- Estatísticas Resumidas -->
                    <div
                        class="summary-stats mb-4"
                        v-if="tableData.length > 0"
                    >
                        <div class="row">
                            <div class="col-md-3">
                                <div class="stat-card">
                                    <div class="stat-icon">
                                        <i class="fas fa-list-alt"></i>
                                    </div>
                                    <div class="stat-content">
                                        <div class="stat-value">{{ tableData.length }}</div>
                                        <div class="stat-label">Motivos Diferentes</div>
                                    </div>
                                </div>
                            </div>
                            <div class="col-md-3">
                                <div class="stat-card">
                                    <div class="stat-icon">
                                        <i class="fas fa-chart-bar"></i>
                                    </div>
                                    <div class="stat-content">
                                        <div class="stat-value">{{ totalReservations }}</div>
                                        <div class="stat-label">Total de Reservas</div>
                                    </div>
                                </div>
                            </div>
                            <div class="col-md-3">
                                <div class="stat-card">
                                    <div class="stat-icon">
                                        <i class="fas fa-star"></i>
                                    </div>
                                    <div class="stat-content">
                                        <div class="stat-value">{{ topReasonPercentage }}%</div>
                                        <div class="stat-label">Motivo Principal</div>
                                    </div>
                                </div>
                            </div>
                            <div class="col-md-3">
                                <div class="stat-card">
                                    <div class="stat-icon">
                                        <i class="fas fa-calculator"></i>
                                    </div>
                                    <div class="stat-content">
                                        <div class="stat-value">{{ avgReservationsPerReason }}</div>
                                        <div class="stat-label">Média por Motivo</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Top 5 Motivos -->
                    <div
                        class="top-reasons mb-4"
                        v-if="tableData.length > 0"
                    >
                        <h6 class="section-title">
                            <i class="fas fa-trophy mr-2"></i>
                            Top 5 Motivos Mais Frequentes
                        </h6>
                        <div class="reasons-grid">
                            <div
                                v-for="(reason, index) in tableData.slice(0, 5)"
                                :key="index"
                                class="reason-card"
                                :class="getReasonCardClass(index)"
                            >
                                <div class="reason-position">{{ index + 1 }}º</div>
                                <div class="reason-content">
                                    <div class="reason-title">{{ reason.description }}</div>
                                    <div class="reason-stats">
                                        <div class="reason-count">
                                            <i class="fas fa-calendar-plus mr-1"></i>
                                            {{ reason.value }} reservas
                                        </div>
                                        <div class="reason-percentage">
                                            {{ getPercentage(reason.value) }}%
                                        </div>
                                    </div>
                                </div>
                                <div class="reason-icon">
                                    <i :class="getReasonIcon(index)"></i>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Gráfico -->
                    <div
                        class="chart-container mb-4"
                        v-if="tableData.length > 0"
                    >
                        <div class="chart-header">
                            <h6 class="chart-title">
                                <i class="fas fa-chart-bar mr-2"></i>
                                Distribuição dos Motivos
                            </h6>
                        </div>
                        <ChartBar
                            v-if="renderChart"
                            :chart-data="chartData"
                            :options="chartOptions"
                            :height="300"
                        />
                    </div>

                    <!-- Tabela Completa -->
                    <div class="reasons-table">
                        <div class="table-responsive">
                            <table class="table table-hover">
                                <thead class="bg-light">
                                <tr>
                                    <th width="5%">#</th>
                                    <th width="60%">Justificativa</th>
                                    <th
                                        width="20%"
                                        class="text-center"
                                    >Quantidade
                                    </th>
                                    <th
                                        width="15%"
                                        class="text-center"
                                    >Percentual
                                    </th>
                                </tr>
                                </thead>
                                <tbody>
                                <tr
                                    v-for="(reason, index) in tableData"
                                    :key="index"
                                >
                                    <td>
                                        <div
                                            class="position-indicator"
                                            :class="getPositionClass(index + 1)"
                                        >
                                            {{ index + 1 }}
                                        </div>
                                    </td>
                                    <td>
                                        <div class="reason-info">
                                            <div class="reason-name">{{ reason.description }}</div>
                                            <div class="reason-meta">
                                                <span
                                                    class="popularity-badge"
                                                    :class="getPopularityClass(reason.value)"
                                                >
                                                    {{ getPopularityLabel(reason.value) }}
                                                </span>
                                            </div>
                                        </div>
                                    </td>
                                    <td class="text-center">
                                        <span class="badge badge-purple text-white">{{ reason.value }}</span>
                                    </td>
                                    <td class="text-center">
                                        <div class="percentage-bar">
                                            <div class="percentage-progress">
                                                <div
                                                    class="progress-fill"
                                                    :style="`width: ${getPercentage(reason.value)}%`"
                                                ></div>
                                            </div>
                                            <span class="percentage-text">{{ getPercentage(reason.value) }}%</span>
                                        </div>
                                    </td>
                                </tr>
                                <tr v-if="tableData.length === 0">
                                    <td
                                        colspan="4"
                                        class="text-center text-muted py-4"
                                    >
                                        Nenhum motivo encontrado para o período selecionado
                                    </td>
                                </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <!-- Análise Detalhada -->
                    <div
                        class="detailed-analysis mt-4"
                        v-if="tableData.length > 0"
                    >
                        <div class="row">
                            <div class="col-md-6">
                                <div class="analysis-card">
                                    <h6 class="analysis-title">
                                        <i class="fas fa-lightbulb mr-2"></i>
                                        Insights dos Dados
                                    </h6>
                                    <div class="analysis-content">
                                        <div class="insight-item">
                                            <div class="insight-label">Motivo Mais Popular:</div>
                                            <div class="insight-value">{{ mostPopularReason }}</div>
                                        </div>
                                        <div class="insight-item">
                                            <div class="insight-label">Concentração Top 3:</div>
                                            <div class="insight-value">{{ top3Concentration }}%</div>
                                        </div>
                                        <div class="insight-item">
                                            <div class="insight-label">Diversidade de Motivos:</div>
                                            <div class="insight-value">{{ diversityLevel }}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div class="col-md-6">
                                <div class="analysis-card">
                                    <h6 class="analysis-title">
                                        <i class="fas fa-chart-pie mr-2"></i>
                                        Distribuição por Categoria
                                    </h6>
                                    <div class="analysis-content">
                                        <div class="category-item">
                                            <div class="category-label">Motivos Principais (Top 3):</div>
                                            <div class="category-value">{{ topReasons }}</div>
                                        </div>
                                        <div class="category-item">
                                            <div class="category-label">Motivos Secundários:</div>
                                            <div class="category-value">{{ secondaryReasons }}</div>
                                        </div>
                                        <div class="category-item">
                                            <div class="category-label">Outros Motivos:</div>
                                            <div class="category-value">{{ otherReasons }}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import ChartBar from "@/components/Utils/ChartBar.js";
import vSelect from "vue-select";
import {getMessage} from "../../composables/message";
import HeaderFilter from "./HeaderFilter.vue";

export default {
    name: "MainReasonsReport", // nome do componente

    components: {
        HeaderFilter,
        ChartBar, // componente de gráfico de barras
        vSelect, // componente de select
    },

    data() {
        return {
            loadingData: false, // controla carregamento dos dados
            renderChart: false, // controla renderização do gráfico

            // tableData - dados para a tabela
            tableData: [],

            // chartData - dados para o gráfico
            chartData: {},

            // chartOptions - opções de configuração do gráfico
            chartOptions: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    legend: {
                        display: false
                    },
                    tooltip: {
                        mode: 'index',
                        intersect: false,
                        backgroundColor: 'rgba(0, 0, 0, 0.8)',
                        titleColor: 'white',
                        bodyColor: 'white',
                        borderColor: 'rgba(255, 255, 255, 0.2)',
                        borderWidth: 1,
                        callbacks: {
                            afterBody: (context) => {
                                const value = context[0].raw;
                                const total = this.totalReservations;
                                const percentage = total > 0 ? Math.round((value / total) * 100) : 0;
                                return `Percentual: ${percentage}%`;
                            }
                        }
                    }
                },
                scales: {
                    y: {
                        beginAtZero: true,
                        min: 0,
                        grid: {
                            color: 'rgba(0, 0, 0, 0.1)'
                        },
                        ticks: {
                            stepSize: 1,
                            callback: function(value) {
                                return Number.isInteger(value) ? value : '';
                            }
                        }
                    },
                    x: {
                        grid: {
                            display: false
                        },
                        ticks: {
                            maxRotation: 45,
                            minRotation: 0
                        }
                    }
                }
            },
        };
    },

    computed: {
        /**
         * Total de reservas / Total reservations
         */
        totalReservations() {
            return this.tableData.reduce((sum, reason) => sum + (reason.value || 0), 0);
        },

        /**
         * Percentual do motivo principal / Top reason percentage
         */
        topReasonPercentage() {
            if (this.tableData.length === 0 || this.totalReservations === 0) return 0;
            return Math.round((this.tableData[0].value / this.totalReservations) * 100);
        },

        /**
         * Média de reservas por motivo / Average reservations per reason
         */
        avgReservationsPerReason() {
            if (this.tableData.length === 0) return 0;
            return Math.round(this.totalReservations / this.tableData.length);
        },

        /**
         * Motivo mais popular / Most popular reason
         */
        mostPopularReason() {
            if (this.tableData.length === 0) return 'N/A';
            const reason = this.tableData[0];
            return reason.description.length > 30
                ? reason.description.substring(0, 30) + '...'
                : reason.description;
        },

        /**
         * Concentração dos top 3 / Top 3 concentration
         */
        top3Concentration() {
            if (this.tableData.length === 0 || this.totalReservations === 0) return 0;
            const top3Total = this.tableData.slice(0, 3).reduce((sum, reason) => sum + reason.value, 0);
            return Math.round((top3Total / this.totalReservations) * 100);
        },

        /**
         * Nível de diversidade / Diversity level
         */
        diversityLevel() {
            if (this.tableData.length === 0) return 'N/A';
            if (this.tableData.length >= 10) return 'Alta';
            if (this.tableData.length >= 5) return 'Média';
            return 'Baixa';
        },

        /**
         * Motivos principais (top 3) / Top reasons
         */
        topReasons() {
            return Math.min(3, this.tableData.length);
        },

        /**
         * Motivos secundários / Secondary reasons
         */
        secondaryReasons() {
            return Math.max(0, Math.min(7, this.tableData.length - 3));
        },

        /**
         * Outros motivos / Other reasons
         */
        otherReasons() {
            return Math.max(0, this.tableData.length - 10);
        },
    },

    mounted() {
        this.loadData(); // carrega dados iniciais
    },

    methods: {
        /**
         * Carrega dados do relatório / Load report data
         */
        async loadData(params) {
            this.loadingData = true;
            this.renderChart = false;

            try {
                const api = this.$store.state.api + "main_reasons";

                const response = await this.$http.get(api, {params});

                this.tableData = response.data.data || [];
                // O ChartBar espera receber os dados diretamente, não um objeto wrapper
                this.chartData = (response.data.graphics && response.data.graphics.problemsperiodPerUser)
                    ? response.data.graphics.problemsperiodPerUser
                    : {};

                // Aguarda um pouco antes de renderizar o gráfico
                setTimeout(() => {
                    this.renderChart = true;
                }, 100);

                // Emite evento para o componente pai / Emit event to parent component
                this.$emit('data-updated', 'main-reasons', response.data);

            } catch (error) {
                console.error('Erro ao carregar principais motivos:', error);
                const message = error.response.data.errors
                    ? getMessage(error.response.data.errors)
                    : error.response.data || 'Erro ao carregar dados';
                this.$message(null, message, "error");
            } finally {
                this.loadingData = false;
            }
        },

        /**
         * Calcula percentual / Calculate percentage
         */
        getPercentage(value) {
            if (this.totalReservations === 0) return 0;
            return Math.round((value / this.totalReservations) * 100);
        },

        /**
         * Retorna classe do card de motivo / Return reason card class
         */
        getReasonCardClass(index) {
            const classes = ['first-reason', 'second-reason', 'third-reason', 'fourth-reason', 'fifth-reason'];
            return classes[index] || 'other-reason';
        },

        /**
         * Retorna ícone do motivo / Return reason icon
         */
        getReasonIcon(index) {
            const icons = [
                'fas fa-crown',
                'fas fa-medal',
                'fas fa-award',
                'fas fa-star',
                'fas fa-thumbs-up'
            ];
            return icons[index] || 'fas fa-circle';
        },

        /**
         * Retorna classe da posição / Return position class
         */
        getPositionClass(position) {
            if (position <= 3) return 'top-position';
            if (position <= 5) return 'good-position';
            return 'normal-position';
        },

        /**
         * Retorna classe de popularidade / Return popularity class
         */
        getPopularityClass(value) {
            const percentage = this.getPercentage(value);
            if (percentage >= 20) return 'very-popular';
            if (percentage >= 10) return 'popular';
            if (percentage >= 5) return 'moderate';
            return 'low';
        },

        /**
         * Retorna label de popularidade / Return popularity label
         */
        getPopularityLabel(value) {
            const percentage = this.getPercentage(value);
            if (percentage >= 20) return 'Muito Popular';
            if (percentage >= 10) return 'Popular';
            if (percentage >= 5) return 'Moderado';
            return 'Pouco Usado';
        },
    },
};
</script>

<style scoped>
/* Estilo principal do relatório */
.main-reasons-report .card {
    border-radius: 12px;
    overflow: hidden;
}

.bg-gradient-purple {
    background: linear-gradient(135deg, #6f42c1 0%, #563d7c 100%);
}

.text-purple {
    color: #6f42c1;
}

.btn-purple {
    background: linear-gradient(135deg, #6f42c1 0%, #563d7c 100%);
    border: none;
    color: white;
}

.btn-purple:hover {
    background: linear-gradient(135deg, #563d7c 0%, #6f42c1 100%);
    color: white;
}

/* Estatísticas resumidas */
.summary-stats {
    margin-bottom: 2rem;
}

.stat-card {
    background: white;
    border-radius: 12px;
    padding: 1.5rem;
    border: 1px solid #e3e6f0;
    display: flex;
    align-items: center;
    gap: 1rem;
    transition: all 0.3s ease;
    height: 100px;
}

.stat-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
}

.stat-icon {
    width: 60px;
    height: 60px;
    border-radius: 12px;
    background: linear-gradient(135deg, #6f42c1 0%, #563d7c 100%);
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.5rem;
}

.stat-value {
    font-size: 1.75rem;
    font-weight: 800;
    color: #2d3748;
    margin-bottom: 0.25rem;
}

.stat-label {
    font-size: 0.875rem;
    color: #718096;
    font-weight: 600;
}

/* Top 5 motivos */
.top-reasons {
    margin-bottom: 2rem;
}

.section-title {
    color: #2d3748;
    font-weight: 700;
    margin-bottom: 1rem;
    border-bottom: 2px solid #e3e6f0;
    padding-bottom: 0.5rem;
}

.reasons-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 1rem;
}

.reason-card {
    background: white;
    border-radius: 12px;
    padding: 1.5rem;
    border: 2px solid #e3e6f0;
    position: relative;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    gap: 1rem;
}

.reason-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
}

.reason-card.first-reason {
    border-color: #ffd700;
    background: linear-gradient(135deg, #fff9e6 0%, #fff3cd 100%);
}

.reason-card.second-reason {
    border-color: #c0c0c0;
    background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
}

.reason-card.third-reason {
    border-color: #cd7f32;
    background: linear-gradient(135deg, #fdf6f0 0%, #f4e5d3 100%);
}

.reason-position {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: linear-gradient(135deg, #6f42c1 0%, #563d7c 100%);
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 700;
    font-size: 1.25rem;
}

.reason-content {
    flex: 1;
}

.reason-title {
    font-weight: 700;
    color: #2d3748;
    margin-bottom: 0.5rem;
    font-size: 1.1rem;
}

.reason-stats {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.reason-count {
    color: #718096;
    font-weight: 600;
    font-size: 0.9rem;
}

.reason-percentage {
    background: linear-gradient(135deg, #6f42c1 0%, #563d7c 100%);
    color: white;
    padding: 0.25rem 0.75rem;
    border-radius: 15px;
    font-size: 0.875rem;
    font-weight: 700;
}

.reason-icon {
    font-size: 1.5rem;
    color: #6f42c1;
}

/* Container do gráfico */
.chart-container {
    background: white;
    border-radius: 12px;
    padding: 1.5rem;
    border: 1px solid #e3e6f0;
}

.chart-header {
    margin-bottom: 1rem;
    border-bottom: 1px solid #e3e6f0;
    padding-bottom: 0.5rem;
}

.chart-title {
    color: #2d3748;
    font-weight: 700;
    margin-bottom: 0;
}

/* Tabela de motivos */
.reasons-table {
    background: white;
    border-radius: 12px;
    overflow: hidden;
}

.table {
    margin-bottom: 0;
}

.table thead th {
    border-top: none;
    font-weight: 600;
    color: #5a5c69;
    background-color: #f8f9fc;
}

.table tbody tr:hover {
    background-color: #f8f9fc;
}

/* Indicador de posição */
.position-indicator {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    border-radius: 50%;
    font-weight: 700;
    color: white;
}

.position-indicator.top-position {
    background: linear-gradient(135deg, #ffd700 0%, #ffed4e 100%);
    color: #b45309;
}

.position-indicator.good-position {
    background: linear-gradient(135deg, #6f42c1 0%, #563d7c 100%);
}

.position-indicator.normal-position {
    background: #6c757d;
}

/* Informações do motivo */
.reason-info .reason-name {
    font-weight: 600;
    color: #2d3748;
    margin-bottom: 0.25rem;
}

.reason-meta {
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.popularity-badge {
    padding: 0.25rem 0.5rem;
    border-radius: 12px;
    font-size: 0.75rem;
    font-weight: 600;
}

.popularity-badge.very-popular {
    background: #d4edda;
    color: #155724;
}

.popularity-badge.popular {
    background: #cce5ff;
    color: #004085;
}

.popularity-badge.moderate {
    background: #fff3cd;
    color: #856404;
}

.popularity-badge.low {
    background: #f8d7da;
    color: #721c24;
}

/* Barra de percentual */
.percentage-bar {
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.percentage-progress {
    flex: 1;
    height: 8px;
    background: #e9ecef;
    border-radius: 4px;
    overflow: hidden;
}

.progress-fill {
    height: 100%;
    background: linear-gradient(135deg, #6f42c1 0%, #563d7c 100%);
    transition: width 0.3s ease;
}

.percentage-text {
    font-size: 0.75rem;
    font-weight: 600;
    color: #495057;
    min-width: 40px;
}

/* Análise detalhada */
.detailed-analysis {
    margin-top: 2rem;
}

.analysis-card {
    background: white;
    border-radius: 12px;
    padding: 1.5rem;
    border: 1px solid #e3e6f0;
    height: 100%;
}

.analysis-title {
    color: #2d3748;
    font-weight: 700;
    margin-bottom: 1rem;
    border-bottom: 1px solid #e3e6f0;
    padding-bottom: 0.5rem;
}

.analysis-content {
    color: #4a5568;
}

.insight-item, .category-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.75rem 0;
    border-bottom: 1px solid #f1f3f4;
}

.insight-item:last-child, .category-item:last-child {
    border-bottom: none;
}

.insight-label, .category-label {
    font-weight: 600;
    color: #4a5568;
}

.insight-value, .category-value {
    font-weight: 700;
    color: #2d3748;
}

/* Badges */
.badge {
    font-size: 0.75rem;
    padding: 0.375rem 0.75rem;
}

.badge-purple {
    background: linear-gradient(135deg, #6f42c1 0%, #563d7c 100%);
    color: #FFF;
}

/* Formulários */
.form-label {
    font-weight: 600;
    color: #5a5c69;
    margin-bottom: 0.5rem;
}

.form-control {
    border-radius: 0.35rem;
    border: 1px solid #d1d3e2;
}

.form-control:focus {
    border-color: #6f42c1;
    box-shadow: 0 0 0 0.2rem rgba(111, 66, 193, 0.25);
}

/* Responsividade */
@media (max-width: 768px) {
    .reasons-grid {
        grid-template-columns: 1fr;
    }

    .reason-card {
        flex-direction: column;
        text-align: center;
        gap: 1rem;
    }

    .reason-stats {
        flex-direction: column;
        gap: 0.5rem;
    }

    .stat-card {
        height: auto;
        padding: 1rem;
    }

    .stat-value {
        font-size: 1.5rem;
    }

    .insight-item, .category-item {
        flex-direction: column;
        align-items: flex-start;
        gap: 0.25rem;
    }

    .percentage-bar {
        flex-direction: column;
        align-items: stretch;
    }

    .table-responsive {
        font-size: 0.875rem;
    }
}
</style>



// WEBPACK FOOTER //
// src/components/Indicators/main_reasons.vue